<script lang="ts">
  // Component for new style of customer service banner
  import { page } from "$app/state";
  import Button from "$lib/Button.svelte";
  import Input from "$lib/InputText.svelte";
  import sessionValues from "$lib/sessionValues";
  import { getUtmSearchParams } from "$lib/utils/utmSearchParams";

  const eightDigitsNotStartingWith0Or1 = `^[2-9]\\d{7}$`;

  const invalidTelEvent = (e: Event) => {
    const eventTarget = e.currentTarget as HTMLInputElement;

    // Set custom error message
    if (eventTarget?.validity?.valueMissing) {
      eventTarget.setCustomValidity("Mobilnummer er påkrevd");
    } else if (eventTarget?.validity?.patternMismatch) {
      eventTarget.setCustomValidity(
        "Mobilnummeret må bestå av 8 siffer, og være et gyldig norsk mobilnummer"
      );
    }
  };

  const invalidTelMessageReset = (e: Event) => {
    const eventTarget = e.currentTarget as HTMLInputElement;
    // Reset custom error message
    if (eventTarget) eventTarget.setCustomValidity("");
  };

  async function sendForm() {
    const data = new FormData(form);
    const mobile = data.get("mobile");
    data.append("origin", page.url.pathname);
    data.append("mobile", `+47${mobile}`);
    const source = page.url.searchParams.get("source") || "ishavskraft.no";
    data.append("source", source);
    data.append("trigger", "pink-banner");
    const utmParams = getUtmSearchParams($sessionValues);

    for (const [key, value] of utmParams) {
      data.append(key, value);
    }

    const body = Object.fromEntries(data.entries());
    const res = await fetch("/api/privat/ringmeg", {
      method: "POST",
      body: JSON.stringify(body),
    });

    if (res.ok) {
      form?.reset();
      formResponse = isBorettslag
        ? `Takk for henvendelsen, vi kontakter deg snarest på ${
            mobile ? mobile.toString().replace(/(\d{3})(\d{2})(\d{3})/, "+47 $1 $2 $3") : "telefon"
          }`
        : (customFeedback ??
          `Takk for henvendelsen, vi kontakter deg på ${
            mobile ? mobile.toString().replace(/(\d{3})(\d{2})(\d{3})/, "+47 $1 $2 $3") : "telefon"
          } så snart en av våre kunderådgivere blir ledig!`);
    } else {
      formResponse = "Noe gikk galt. Vennligst prøv igjen eller kontakt oss om problemet vedvarer.";
    }
  }

  interface Props {
    contactForm?: boolean;
    customFeedback?: string | undefined;
    showNameInput?: boolean;
    showMessageInput?: boolean;
    isBorettslag?: boolean;
    children?: import("svelte").Snippet;
    formPostElements?: import("svelte").Snippet;
  }

  let {
    contactForm = true,
    customFeedback = undefined,
    showNameInput = true,
    showMessageInput = true,
    isBorettslag = false,
    children,
    formPostElements,
  }: Props = $props();
  let form: HTMLFormElement | undefined = $state();
  let formResponse: string | undefined = $state();
</script>

{#if contactForm}
  <form
    method="POST"
    onsubmit={(e) => {
      e.preventDefault();
      sendForm();
    }}
    bind:this={form}
  >
    <p class="phone">
      <label for="mobile-input">Mobilnummer</label>
      <Input
        type="tel"
        name="mobile"
        id="mobile-input"
        placeholder="Ditt mobilnummer"
        required
        pattern={eightDigitsNotStartingWith0Or1}
        oninvalid={invalidTelEvent}
        oninput={invalidTelMessageReset}
      />
    </p>

    <p class="name">
      {#if showNameInput}
        <label for="name-input">Navn</label>
        <Input
          type="text"
          name="name"
          id="name-input"
          placeholder="Ditt navn (valgfritt)"
          autocomplete="off"
        />
      {/if}
    </p>

    {#if showMessageInput}
      <p class="message">
        <label for="message-input">Navn på borettslag</label>
        <Input
          name="message"
          id="message-input"
          placeholder="Navn på borettslag"
          autocomplete="off"
        />
      </p>
    {/if}

    <fieldset class="role rounded-1rem border-none p-0">
      <legend>Er du styremedlem?</legend>

      <div class="flex gap-x-1 flex-wrap">
        <p class="button button-link flex min-w-3rem px-0 gap-x-1">
          <input type="radio" name="styremedlem" id="styremedlem-ja" value="true" checked />
          <label for="styremedlem-ja">Ja</label>
        </p>

        <p class="button button-link flex min-w-3rem px-0 gap-x-1">
          <input type="radio" name="styremedlem" id="styremedlem-nei" value="false" />
          <label for="styremedlem-nei">Nei</label>
        </p>
      </div>
    </fieldset>

    {@render formPostElements?.()}

    <p class="submit col-span-2">
      <Button type="submit" class="whitespace-nowrap">Send inn</Button>
    </p>
  </form>

  {#if formResponse}
    <p>{formResponse}</p>
  {/if}
{/if}

{@render children?.()}

<style lang="postcss">
  form {
    label + :global(input) {
      margin-block-start: var(--s-1);
      width: 100%;
    }

    p,
    fieldset {
      margin-block: var(--s-2);
    }
  }

  @media (--tablet) {
    form {
      display: grid;
      gap: 0 var(--s-8);
      grid-template-areas:
        "phone name"
        "message message"
        "role _"
        "submit _";
    }

    .phone {
      grid-area: phone;
    }

    .name {
      grid-area: name;
    }

    .message {
      grid-area: message;
    }

    .role {
      grid-area: role;
    }
  }
</style>
